import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createPaxType,
  getAllPaxTypes,
  getPaxTypeByID,
  updatePaxType,
} from "./services/paxType.services";
import { clearPaxTypeDetails } from "./paxTypeSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const PaxCountChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const Adult = formRenderProps.valueGetter("Adult");
  const ExtraAdult = formRenderProps.valueGetter("ExtraAdult");
  const CWB = formRenderProps.valueGetter("CWB");
  const CNB = formRenderProps.valueGetter("CNB");
  const Infant = formRenderProps.valueGetter("Infant");
  useEffect(() => {
    formRenderProps.onChange("PaxCount", {
      value: +Adult + +ExtraAdult + +CWB + +CNB + +Infant,
    });
  }, [Adult, ExtraAdult, CWB, CNB, Infant]);
  return null;
};

const CreatePaxType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const PaxTypeDetail = useAppSelector((state) => state.paxType.PaxTypeDetail);
  const PaxTypeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.paxType.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (PaxTypeID) {
      dispatch(getPaxTypeByID(PaxTypeID));
    }
  }, [PaxTypeID]);

  useEffect(() => {
    return () => {
      dispatch(clearPaxTypeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PaxTypeDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!PaxTypeID) {
        const insertPayload = {
          PaxType: values.PaxType,
          PaxCount: values.PaxCount ? +values.PaxCount : null,
          Adult: values.Adult ? +values.Adult : 0,
          ExtraAdult: values.ExtraAdult ? +values.ExtraAdult : 0,
          CWB: values.CWB ? +values.CWB : 0,
          CNB: values.CNB ? +values.CNB : 0,
          Infant: values.Infant ? +values.Infant : 0,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createPaxType(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearPaxTypeDetails());
          dispatch(getAllPaxTypes());
        }
      } else {
        const editPayload = {
          ID: PaxTypeID,
          PaxType: values.PaxType,
          PaxCount: values.PaxCount ? +values.PaxCount : null,
          Adult: values.Adult ? +values.Adult : 0,
          ExtraAdult: values.ExtraAdult ? +values.ExtraAdult : 0,
          CWB: values.CWB ? +values.CWB : 0,
          CNB: values.CNB ? +values.CNB : 0,
          Infant: values.Infant ? +values.Infant : 0,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updatePaxType(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearPaxTypeDetails());
          dispatch(getAllPaxTypes());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={PaxTypeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {PaxTypeID ? "Update Pax Type" : "Add Pax Type"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="PaxType"
                  label="Pax Type"
                  placeholder="Pax Type"
                  astrike={true}
                  component={FormTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{ width: "100%", display: "flex", gap: "10px" }}
              >
                <Field
                  wrapperClassName="w-100 right-aligned-field"
                  name="Adult"
                  label="Adult"
                  placeholder="2"
                  type="number"
                  astrike={true}
                  component={FormTextField}
                  validator={requiredValidator}
                />
                <Field
                  wrapperClassName="w-100 right-aligned-field"
                  name="ExtraAdult"
                  label="Extra Adult"
                  type="number"
                  placeholder="0"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{ width: "100%", display: "flex", gap: "10px" }}
              >
                <Field
                  wrapperClassName="w-100 right-aligned-field"
                  name="CWB"
                  label="Child With Bed"
                  placeholder="0"
                  type="number"
                  component={FormTextField}
                />
                <Field
                  wrapperClassName="w-100 right-aligned-field"
                  name="CNB"
                  label="Child With No Bed"
                  placeholder="0"
                  type="number"
                  component={FormTextField}
                />
                <Field
                  wrapperClassName="w-100 right-aligned-field"
                  name="Infant"
                  label="Infant"
                  type="number"
                  placeholder="0"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  wrapperClassName="right-aligned-field"
                  name="PaxCount"
                  label="Pax Count"
                  type="number"
                  placeholder="0"
                  component={FormTextField}
                  disabled={"true"}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={!PaxTypeID ? "Save" : "Update"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
            <PaxCountChangeWatcher formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
    </>
  );
};

export default CreatePaxType;
